import { useEffect, useState, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import CompanyFeaturePanelComponent from "./CompanyFeaturePanel.component";

const CompanyFeaturePanelContainer = forwardRef(({ features, onDataChange, disabled, enabledList = null, layer = 3 }, ref) => {
  const removeKeysRecursively = (data, keysToRemove) => {
    if (Array.isArray(data)) {
      return data.map((item) => removeKeysRecursively(item, keysToRemove));
    }

    if (typeof data === "object" && data !== null) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        const newAcc = { ...acc };
        if (!keysToRemove.includes(key)) {
          newAcc[key] = removeKeysRecursively(value, keysToRemove);
        }
        return newAcc;
      }, {});
    }

    return data;
  };

  const initializeEnabledState = (feats) => {
    const state = {};
    const enabledArray = Array.isArray(enabledList) ? enabledList : [];
    const processItem = (prefix, item) => {
      const itemName = prefix ? `${prefix}.${item.name}` : item.name;
      state[itemName] = enabledList ? enabledArray.includes(itemName) : (item.isEnabled ?? false);

      if (item.functions) {
        item.functions.forEach((func) => {
          processItem(itemName, func);
        });
      }
      if (item.actions) {
        item.actions.forEach((action) => {
          processItem(itemName, action);
        });
      }
      if (item.subActions) {
        item.subActions.forEach((subAction) => {
          processItem(itemName, subAction);
        });
      }
      if (item.subModules) {
        item.subModules.forEach((subModule) => {
          processItem(itemName, subModule);
        });
      }
    };

    feats.forEach((module) => processItem('', module));
    return state;
  };

  const [featuresTrimmed, setFeaturesTrimmed] = useState(() => removeKeysRecursively(features, ['module', 'subModules', 'functions', 'actions', 'subActions'].slice(layer, 5)));
  const [enabled, setEnabled] = useState(() => initializeEnabledState(featuresTrimmed));
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const temp = removeKeysRecursively(features, ['module', 'subModules', 'functions', 'actions', 'subActions'].slice(layer, 5));
    setFeaturesTrimmed(temp);
    setEnabled((prevEnabled) => {
      const newEnabled = initializeEnabledState(temp);
      const prevKeys = Object.keys(prevEnabled || {});
      const newKeys = Object.keys(newEnabled);
      if (
        prevKeys.length !== newKeys.length ||
        prevKeys.some((k, i) => k !== newKeys[i])
      ) {
        return newEnabled;
      }
      return prevEnabled;
    });
  }, [features, layer]);

  const onChange = useCallback((name, isChecked, isFunction) => {
    const temp = { ...enabled };
    temp[name] = isChecked;

    const level = isFunction ? 3 : name.split('.').length;

    const updateDescendants = () => {
      Object.keys(temp).forEach((key) => {
        if (key.startsWith(`${name}.`) && key !== name) {
          if (isChecked && key.endsWith('read') && (key.split('.').length === 2 || key.split('.').length === 3)) {
            const writeKey = key.replace('read', 'write');
            if (temp[writeKey] === undefined) {
              temp[key] = isChecked;
            }
          } else {
            temp[key] = isChecked;
          }
        }
      });
    };

    const handleMutuallyExclusiveFunctions = () => {
      if (isFunction) {
        // Find the function level (read or write) in the name
        const parts = name.split('.');
        let functionIndex = -1;
        for (let i = 0; i < parts.length; i++) {
          if (parts[i] === 'read' || parts[i] === 'write') {
            functionIndex = i;
            break;
          }
        }

        if (functionIndex === -1) return;

        const currentFunction = parts[functionIndex];
        const oppositeFunction = currentFunction === 'read' ? 'write' : 'read';

        // Build the prefix up to the function level
        const prefix = parts.slice(0, functionIndex).join('.');
        const oppositeKey = prefix ? `${prefix}.${oppositeFunction}` : oppositeFunction;

        // Disable the opposite function and all its children
        Object.keys(temp).forEach((key) => {
          if (key === oppositeKey || key.startsWith(`${oppositeKey}.`)) {
            temp[key] = false;
          }
        });
      }
    };

    const enableAncestors = () => {
      if (isChecked) {
        name.split('.').forEach((_, i) => {
          const ancestorKey = name.split('.').slice(0, i + 1).join('.');
          temp[ancestorKey] = true;
        });
      }
    };

    const disableModuleIfSiblingsDisabled = () => {
      if (!isChecked && level === 2) {
        const moduleName = name.split('.')[0];
        const isAllDisabled = Object.keys(temp).filter((key) =>
          key !== moduleName && key.startsWith(moduleName) && temp[key]
        ).length === 0;
        temp[moduleName] = !isAllDisabled;
      }
    };

    updateDescendants();
    handleMutuallyExclusiveFunctions();
    enableAncestors();
    disableModuleIfSiblingsDisabled();

    setEnabled(temp);
    onDataChange(temp);
  }, [enabled, onDataChange]);

  const selectAll = () => {
    const temp = { ...enabled };

    // First, enable all items
    Object.keys(temp).forEach((key) => {
      temp[key] = true;
    });

    // Then handle mutually exclusive read/write - prefer 'write' over 'read'
    Object.keys(temp).forEach((key) => {
      if (key.endsWith('.read')) {
        const writeKey = key.replace('.read', '.write');
        if (temp[writeKey] !== undefined) {
          // Disable read and its children, keep write enabled
          temp[key] = false;
          Object.keys(temp).forEach((k) => {
            if (k.startsWith(`${key}.`)) {
              temp[k] = false;
            }
          });
        }
      }
    });

    setEnabled(temp);
    onDataChange(temp);
  };

  const deselectAll = () => {
    const temp = { ...enabled };

    Object.keys(temp).forEach((key) => {
      temp[key] = false;
    });

    setEnabled(temp);
    onDataChange(temp);
  };

  useImperativeHandle(ref, () => ({
    selectAll,
    deselectAll,
  }));

  return <CompanyFeaturePanelComponent
    features={featuresTrimmed}
    enabled={enabled}
    onChange={onChange}
    disabled={disabled}
    layer={layer}
  />;
});

export default CompanyFeaturePanelContainer;
